import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  People,
  TrendingUp,
  Security,
  Analytics,
  Assignment,
  Notifications,
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Spa,
  Work,
  MonetizationOn,
  Psychology,
  AutoAwesome,
  Refresh,
  Dashboard,
  Room,
  SensorDoor,
  VpnKey,
  Task,
  Storage as DatabaseIcon,
  Cloud,
  Wifi,
  Storage,
  Speed,
  Eco,
  Lightbulb,
  Thermostat,
  Opacity,
  Visibility,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard, FeatureCard } from './common/ModernCard';
import ModernButton from './common/ModernButton';
import axiosInstance from './axiosConfig';
import { useLanguage } from '../contexts/LanguageContext';

const DashboardOverview = () => {
  const { translate } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [systemData, setSystemData] = useState({
    employees: { active: 0, total: 0, trend: 0 },
    visitors: { today: 0, week: 0, trend: 0 },
    sensors: { active: 0, total: 0, health: 0 },
    tasks: { pending: 0, completed: 0, overdue: 0 },
    projects: { active: 0, completed: 0, revenue: 0 },
    access: { granted: 0, denied: 0, today: 0 },
    ai: { insights: [], predictions: [], recommendations: [] },
    system: { status: 'online', uptime: 0, performance: 0 }
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from your APIs
      const [employeesRes, sensorsRes, tasksRes, accessRes] = await Promise.allSettled([
        axiosInstance.get('/api/employees/stats'),
        axiosInstance.get('/api/sensors/status'),
        axiosInstance.get('/api/tasks/overview'),
        axiosInstance.get('/api/access/logs/recent')
      ]);

      // Process responses and set data
      const newData = { ...systemData };
      
      if (employeesRes.status === 'fulfilled') {
        newData.employees = employeesRes.value.data;
      }
      
      if (sensorsRes.status === 'fulfilled') {
        newData.sensors = sensorsRes.value.data;
      }
      
      if (tasksRes.status === 'fulfilled') {
        newData.tasks = tasksRes.value.data;
      }
      
      if (accessRes.status === 'fulfilled') {
        newData.access = accessRes.value.data;
        setRecentActivities(accessRes.value.data.recent || []);
      }

      // Generate AI insights
      generateAiInsights(newData);
      
      setSystemData(newData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAiInsights = (data) => {
    const insights = [];
    
    // Employee insights
    if (data.employees.trend > 10) {
      insights.push({
        type: 'success',
        icon: <People />,
        title: 'Crecimiento de Personal',
        message: `El equipo ha crecido ${data.employees.trend}% este mes`,
        action: 'Ver empleados'
      });
    }
    
    // Sensor insights
    if (data.sensors.health < 80) {
      insights.push({
        type: 'warning',
        icon: <Analytics />,
        title: 'Mantenimiento de Sensores',
        message: `${data.sensors.total - data.sensors.active} sensores requieren atención`,
        action: 'Revisar sensores'
      });
    }
    
    // Task insights
    if (data.tasks.overdue > 0) {
      insights.push({
        type: 'error',
        icon: <Assignment />,
        title: 'Tareas Vencidas',
        message: `${data.tasks.overdue} tareas están vencidas`,
        action: 'Ver tareas'
      });
    }
    
    // Access insights
    if (data.access.denied > data.access.granted * 0.1) {
      insights.push({
        type: 'warning',
        icon: <Security />,
        title: 'Actividad Sospechosa',
        message: 'Aumento en intentos de acceso denegados',
        action: 'Revisar logs'
      });
    }

    setAiInsights(insights);
  };

  const stats = [
    {
      title: translate('activeEmployees'),
      value: systemData.employees.active.toString(),
      subtitle: `${systemData.employees.trend > 0 ? '+' : ''}${systemData.employees.trend}% ${translate('thisMonth')}`,
      icon: <People />,
      trend: systemData.employees.trend > 0 ? 'up' : 'down',
      trendValue: `${systemData.employees.trend > 0 ? '+' : ''}${systemData.employees.trend}%`,
      color: 'primary',
      action: () => navigate('/facility/employments'),
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: translate('visitorsToday'),
      value: systemData.visitors.today.toString(),
      subtitle: `${systemData.visitors.week} ${translate('thisWeek')}`,
      icon: <Security />,
      trend: systemData.visitors.trend > 0 ? 'up' : 'down',
      trendValue: `${systemData.visitors.trend > 0 ? '+' : ''}${systemData.visitors.trend}%`,
      color: 'success',
      action: () => navigate('/facility/visitors'),
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      title: translate('activeSensors'),
      value: `${systemData.sensors.active}/${systemData.sensors.total}`,
      subtitle: `${systemData.sensors.health}% ${translate('health')}`,
      icon: <Analytics />,
      trend: systemData.sensors.health > 80 ? 'up' : 'down',
      trendValue: `${systemData.sensors.health}%`,
      color: 'info',
      action: () => navigate('/sensors'),
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: translate('pendingTasks'),
      value: systemData.tasks.pending.toString(),
      subtitle: `${systemData.tasks.completed} ${translate('completed')}`,
      icon: <Assignment />,
      trend: systemData.tasks.overdue > 0 ? 'down' : 'up',
      trendValue: systemData.tasks.overdue > 0 ? `${systemData.tasks.overdue} ${translate('overdue')}` : translate('onTrack'),
      color: 'warning',
      action: () => navigate('/task-management'),
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    }
  ];

  const quickActions = [
    {
      title: 'AI Access Worker',
      description: 'Acceso inteligente con reconocimiento facial',
      icon: <Psychology />,
      color: 'primary',
      action: () => navigate('/access-worker-ai'),
      badge: 'AI'
    },
    {
      title: 'Gestión de Proyectos',
      description: 'Administrar proyectos y recursos',
      icon: <Spa />,
      color: 'success',
      action: () => navigate('/modular-dashboard'),
      badge: 'Grow'
    },
    {
      title: 'Control de Sensores',
      description: 'Monitoreo en tiempo real',
      icon: <Analytics />,
      color: 'info',
      action: () => navigate('/sensor-status'),
      badge: 'IoT'
    },
    {
      title: 'Gestión de Tareas',
      description: 'Asignar y rastrear tareas',
      icon: <Task />,
      color: 'warning',
      action: () => navigate('/task-dashboard'),
      badge: 'New'
    },
  ];

  const systemModules = [
    {
      title: 'Grow Management',
      icon: <Spa />,
      status: 'active',
      description: 'Gestión de invernaderos y cultivos',
      metrics: { projects: systemData.projects.active, sensors: 12, automation: 8 },
      color: 'success',
      action: () => navigate('/modular-dashboard')
    },
    {
      title: 'Facility Management',
      icon: <Work />,
      status: 'active',
      description: 'Control de acceso y empleados',
      metrics: { employees: systemData.employees.active, visitors: systemData.visitors.today, access: systemData.access.today },
      color: 'primary',
      action: () => navigate('/facility/employments')
    },
    {
      title: 'Task Management',
      icon: <Task />,
      status: systemData.tasks.overdue > 0 ? 'warning' : 'active',
      description: 'Gestión de proyectos y tareas',
      metrics: { pending: systemData.tasks.pending, completed: systemData.tasks.completed, overdue: systemData.tasks.overdue },
      color: systemData.tasks.overdue > 0 ? 'error' : 'warning',
      action: () => navigate('/task-dashboard')
    },
    {
      title: 'IoT Sensors',
      icon: <Analytics />,
      status: systemData.sensors.health > 90 ? 'active' : 'warning',
      description: 'Monitoreo de sensores y automatización',
      metrics: { active: systemData.sensors.active, total: systemData.sensors.total, health: systemData.sensors.health },
      color: 'info',
      action: () => navigate('/sensor-status')
    },
  ];

  const features = [
    {
      title: translate('accessValidation'),
      description: translate('accessValidationDescription'),
      icon: <VpnKey />,
      color: 'primary',
      action: () => navigate('/facility/employee-access'),
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      title: translate('cameraManagement'),
      description: translate('cameraManagementDescription'),
      icon: <Security />,
      color: 'secondary',
      action: () => navigate('/unifi-control'),
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      title: translate('sensorAdoption'),
      description: translate('sensorAdoptionDescription'),
      icon: <Analytics />,
      color: 'success',
      action: () => navigate('/sensors'),
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      title: translate('roomDisplayManager'),
      description: translate('roomDisplayManagerDescription'),
      icon: <Room />,
      color: 'info',
      action: () => navigate('/room-display-manager'),
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    },
    {
      title: translate('taskManagement'),
      description: translate('taskManagementDescription'),
      icon: <Task />,
      color: 'warning',
      action: () => navigate('/task-management'),
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
    },
    {
      title: translate('databaseUpgrade'),
      description: translate('databaseUpgradeDescription'),
      icon: <DatabaseIcon />,
      color: 'error',
      action: () => navigate('/database-upgrade'),
      gradient: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'warning':
        return <Warning color="warning" />;
      case 'error':
        return <Error color="error" />;
      default:
        return <Schedule color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <ModernLayout title="HarvestAI - Dashboard Principal">
      {/* Header with Refresh */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            🤖 HarvestAI Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Sistema Inteligente de Gestión Integral
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitoreo en tiempo real • IA Integrada • Gestión Unificada
          </Typography>
        </Box>
        <Tooltip title="Actualizar datos">
          <IconButton 
            onClick={fetchDashboardData} 
            disabled={loading}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': { background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)' }
            }}
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* AI Insights Banner */}
      {aiInsights.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {aiInsights.slice(0, 2).map((insight, index) => (
            <Alert 
              key={index}
              severity={insight.type} 
              icon={insight.icon}
              sx={{ mb: 1 }}
              action={
                <ModernButton 
                  variant="outlined" 
                  size="small" 
                  onClick={insight.action}
                >
                  {insight.action}
                </ModernButton>
              }
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {insight.title}
              </Typography>
              {insight.message}
            </Alert>
          ))}
        </Box>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <StatsCard
              title={stat.title}
              value={stat.value}
              subtitle={stat.subtitle}
              icon={stat.icon}
              trend={stat.trend}
              trendValue={stat.trendValue}
              color={stat.color}
              onClick={stat.action}
              gradient={stat.gradient}
            />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* System Modules */}
        <Grid item xs={12} lg={8}>
          <ModernCard
            title="Módulos del Sistema"
            subtitle="Estado de todos los módulos integrados"
            action={
              <ModernButton variant="text" size="small">
                Ver detalles
              </ModernButton>
            }
          >
            <Grid container spacing={2}>
              {systemModules.map((module, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
                      border: `2px solid ${module.status === 'active' ? '#4caf50' : module.status === 'warning' ? '#ff9800' : '#f44336'}`
                    }}
                    onClick={module.action}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ 
                          p: 1, 
                          borderRadius: 1, 
                          background: `linear-gradient(135deg, ${module.color === 'success' ? '#4caf50' : module.color === 'primary' ? '#667eea' : module.color === 'warning' ? '#ff9800' : '#f44336'} 0%, ${module.color === 'success' ? '#45a049' : module.color === 'primary' ? '#764ba2' : module.color === 'warning' ? '#f57c00' : '#d32f2f'} 100%)`,
                          color: 'white',
                          mr: 2
                        }}>
                          {module.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {module.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {module.description}
                          </Typography>
                        </Box>
                        <Chip 
                          label={module.status === 'active' ? 'Activo' : module.status === 'warning' ? 'Atención' : 'Error'} 
                          color={module.status === 'active' ? 'success' : module.status === 'warning' ? 'warning' : 'error'} 
                          size="small" 
                        />
                      </Box>
                      
                      <Grid container spacing={1}>
                        {Object.entries(module.metrics).map(([key, value]) => (
                          <Grid item xs={4} key={key}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, color: module.color }}>
                                {value}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </ModernCard>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} lg={4}>
          <ModernCard
            title="Acciones Rápidas"
            subtitle="Acceso directo a funciones principales"
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {quickActions.map((action, index) => (
                <FeatureCard
                  key={index}
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {action.title}
                      {action.badge && (
                        <Chip 
                          label={action.badge} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                    </Box>
                  }
                  description={action.description}
                  icon={action.icon}
                  color={action.color}
                  action={
                    <ModernButton
                      variant="outlined"
                      size="small"
                      onClick={action.action}
                    >
                      Acceder
                    </ModernButton>
                  }
                />
              ))}
            </Box>
          </ModernCard>
        </Grid>
      </Grid>

      {/* Features Grid */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
          {translate('systemControl')}
        </Typography>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <FeatureCard
                title={feature.title}
                description={feature.description}
                icon={feature.icon}
                gradient={feature.gradient}
                onClick={feature.action}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Activities */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <ModernCard
            title="Actividad Reciente"
            subtitle="Últimas actividades del sistema"
            action={
              <ModernButton variant="text" size="small">
                Ver todas
              </ModernButton>
            }
          >
            {recentActivities.length > 0 ? (
              <List sx={{ p: 0 }}>
                {recentActivities.slice(0, 5).map((activity, index) => (
                  <React.Fragment key={activity.id || index}>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            background: activity.type === 'access' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' :
                                     activity.type === 'sensor' ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' :
                                     'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                          }}
                        >
                          {activity.type === 'access' ? <VpnKey /> :
                           activity.type === 'sensor' ? <Analytics /> : <Notifications />}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.title}
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary">
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" display="block" color="text.secondary">
                              {activity.timestamp}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                    {index < Math.min(recentActivities.length, 5) - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No hay actividad reciente
                </Typography>
              </Box>
            )}
          </ModernCard>
        </Grid>
      </Grid>

      {/* System Performance */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <ModernCard
            title="Rendimiento del Sistema"
            subtitle="Métricas en tiempo real"
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      margin: '0 auto 1rem',
                    }}
                  >
                    <Speed />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Rendimiento
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={systemData.system.performance || 95} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {systemData.system.performance || 95}%
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      margin: '0 auto 1rem',
                    }}
                  >
                    <Cloud />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Estado del Sistema
                  </Typography>
                  <Chip 
                    label={systemData.system.status === 'online' ? 'Online' : 'Offline'} 
                    color={systemData.system.status === 'online' ? 'success' : 'error'} 
                    size="small" 
                  />
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      margin: '0 auto 1rem',
                    }}
                  >
                    <Storage />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Almacenamiento
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={75} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    75% usado
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      margin: '0 auto 1rem',
                    }}
                  >
                    <AutoAwesome />
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    IA Activa
                  </Typography>
                  <Chip 
                    label="Operativa" 
                    color="success" 
                    size="small" 
                    icon={<Psychology />}
                  />
                </Box>
              </Grid>
            </Grid>
          </ModernCard>
        </Grid>
      </Grid>
    </ModernLayout>
  );
};

export default DashboardOverview; 